/**
 * useSEO Hook
 * Manages document.head meta tags, canonical URLs, OG/Twitter tags,
 * and JSON-LD structured data via useEffect with cleanup.
 */

import { OG_IMAGE, TWITTER_IMAGE, WEB_URL } from '@parlay-picker/shared/appConfig';
import { TIER_PICKS_QUOTAS } from '@parlay-picker/shared/subscriptionTier';
import { useEffect } from 'react';

const SITE_URL = WEB_URL;

const DEFAULTS = {
  title: 'AI Parlay Picker - Smart NBA Betting Analysis & Tracking',
  description: `Generate winning NBA parlays with AI-powered picks. Track bets in real-time, analyze performance, and find the best odds across sportsbooks. Free tier includes ${TIER_PICKS_QUOTAS.free.standard} picks daily.`,
  ogTitle: 'AI Parlay Picker - Smart NBA Betting Analysis',
  ogDescription:
    'Generate winning NBA parlays with AI-powered picks. Track bets in real-time and analyze your performance.',
  ogImage: OG_IMAGE,
  twitterTitle: 'AI Parlay Picker - Smart NBA Betting',
  twitterDescription: 'Generate winning NBA parlays with AI-powered picks and real-time tracking.',
  twitterImage: TWITTER_IMAGE,
};

export interface SEOConfig {
  title: string;
  description: string;
  canonical?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  twitterTitle?: string;
  twitterDescription?: string;
  twitterImage?: string;
  noindex?: boolean;
  // biome-ignore lint/suspicious/noExplicitAny: JSON-LD schemas have deeply nested typed structures
  jsonLd?: Record<string, any> | Record<string, any>[];
}

function setMetaTag(attribute: string, key: string, content: string): void {
  let element = document.querySelector(`meta[${attribute}="${key}"]`);
  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attribute, key);
    document.head.appendChild(element);
  }
  element.setAttribute('content', content);
}

function removeMetaTag(attribute: string, key: string): void {
  const element = document.querySelector(`meta[${attribute}="${key}"]`);
  if (element) element.remove();
}

export function useSEO(config: SEOConfig): void {
  useEffect(() => {
    // Title
    document.title = config.title;

    // Meta description
    setMetaTag('name', 'description', config.description);

    // Canonical URL
    let canonicalLink = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (config.canonical) {
      if (!canonicalLink) {
        canonicalLink = document.createElement('link');
        canonicalLink.setAttribute('rel', 'canonical');
        document.head.appendChild(canonicalLink);
      }
      canonicalLink.setAttribute('href', config.canonical);
    } else if (canonicalLink) {
      canonicalLink.remove();
    }

    // Noindex
    if (config.noindex) {
      setMetaTag('name', 'robots', 'noindex, nofollow');
    } else {
      removeMetaTag('name', 'robots');
    }

    // Open Graph
    setMetaTag('property', 'og:title', config.ogTitle ?? config.title);
    setMetaTag('property', 'og:description', config.ogDescription ?? config.description);
    setMetaTag('property', 'og:url', config.canonical ?? SITE_URL);
    if (config.ogImage) {
      setMetaTag('property', 'og:image', config.ogImage);
    }

    // Twitter Card
    setMetaTag('name', 'twitter:title', config.twitterTitle ?? config.title);
    setMetaTag('name', 'twitter:description', config.twitterDescription ?? config.description);
    if (config.twitterImage) {
      setMetaTag('name', 'twitter:image', config.twitterImage);
    }

    // JSON-LD structured data
    const jsonLdScripts: HTMLScriptElement[] = [];
    if (config.jsonLd) {
      const items = Array.isArray(config.jsonLd) ? config.jsonLd : [config.jsonLd];
      for (const item of items) {
        const script = document.createElement('script');
        script.type = 'application/ld+json';
        script.dataset.seo = 'dynamic';
        script.textContent = JSON.stringify(item);
        document.head.appendChild(script);
        jsonLdScripts.push(script);
      }
    }

    // Cleanup: restore defaults on unmount / route change
    return () => {
      document.title = DEFAULTS.title;
      setMetaTag('name', 'description', DEFAULTS.description);
      setMetaTag('property', 'og:title', DEFAULTS.ogTitle);
      setMetaTag('property', 'og:description', DEFAULTS.ogDescription);
      setMetaTag('property', 'og:url', SITE_URL);
      setMetaTag('property', 'og:image', DEFAULTS.ogImage);
      setMetaTag('name', 'twitter:title', DEFAULTS.twitterTitle);
      setMetaTag('name', 'twitter:description', DEFAULTS.twitterDescription);
      setMetaTag('name', 'twitter:image', DEFAULTS.twitterImage);
      removeMetaTag('name', 'robots');

      // Remove canonical (index.html has a static one)
      const canon = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
      if (canon) canon.setAttribute('href', `${SITE_URL}/`);

      // Remove dynamic JSON-LD scripts
      for (const script of jsonLdScripts) {
        script.remove();
      }
    };
  }, [
    config.title,
    config.description,
    config.canonical,
    config.ogTitle,
    config.ogDescription,
    config.ogImage,
    config.twitterTitle,
    config.twitterDescription,
    config.twitterImage,
    config.noindex,
    config.jsonLd,
  ]);
}
